import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { MARKET_GRAPH_BASE_URL } from 'constants/locations';
import { getIsMarketGraphsDarkThemeEnabled } from 'redux/modules/appConfigs/selectors';
import { MarketGraphMobilePopupItem } from 'types/marketGraph';

import styles from './MarketGraphPopUp.module.scss';

type MarketGraphPopUpProps = {
  defaultValue: string | number;
  title: string;
  onSave: (value: string | number) => void;
  isSelectionPage?: boolean;
  items: MarketGraphMobilePopupItem[];
  hasAutoScroll?: boolean;
};

export default function MarketGraphPopUp({
  defaultValue,
  onSave,
  title,
  items,
  isSelectionPage = false,
  hasAutoScroll = false
}: MarketGraphPopUpProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { marketId, selectionId, handicap = 0 } = useParams();
  const location = useLocation();
  const isDarkTheme = useSelector(getIsMarketGraphsDarkThemeEnabled);

  const [currentValue, setCurrentValue] = useState(defaultValue);

  const isSaveDisabled = currentValue === defaultValue;
  const listRef = useRef<HTMLDivElement>(null);

  const goBack = () => {
    if (marketId && selectionId) {
      navigate(`${MARKET_GRAPH_BASE_URL}/${marketId}/${selectionId || 0}/${handicap || 0}${location.search}`, {
        replace: true
      });
    } else {
      navigate(-1);
    }
  };

  const handleSave = () => {
    onSave(currentValue);
  };

  const handleSelectValue = ({ value, isDisabled }: { value: string | number; isDisabled?: boolean }) => {
    if (!isDisabled) {
      setCurrentValue(value);
    }
  };

  useEffect(() => {
    if (listRef.current && hasAutoScroll) {
      const list = listRef.current;
      const listItems = Array.from(list.children);
      const hasVerticalScroll = list.scrollHeight > list.clientHeight;
      const firstEnabledItem = listItems.find(item => !item.classList.contains(styles.popUp__item__disabled || ''));

      if (hasVerticalScroll && firstEnabledItem) {
        firstEnabledItem.scrollIntoView({ block: 'center' });
      }
    }
  }, [items]);

  return (
    <>
      <Helmet>
        <title>
          {t('general.page.title')} | {t('marketGraph.labels.title')}
        </title>
      </Helmet>
      <div className={styles.popUp}>
        <div className={classNames(styles.popUp__header, { [styles.popUp__header__darkTheme]: isDarkTheme })}>
          <button
            onClick={goBack}
            className={classNames(styles.popUp__header__btn, { [styles.popUp__header__btn__darkTheme]: isDarkTheme })}
          >
            <i
              className={classNames('biab_custom-icon-chevron-left-2', styles.popUp__header__btn__icon, {
                [styles.popUp__header__btn__icon__darkTheme]: isDarkTheme
              })}
            />
          </button>
          <p
            className={classNames(styles.popUp__header__title, {
              [styles.popUp__header__title__darkTheme]: isDarkTheme
            })}
          >
            {title}
          </p>
          <button
            onClick={handleSave}
            className={classNames(styles.popUp__header__btn, styles.popUp__header__btn__right, {
              [styles.popUp__header__btn__right__darkTheme]: isDarkTheme
            })}
            disabled={isSaveDisabled}
          >
            <i
              className={classNames(
                'biab_custom-icon-checkmark',
                styles.popUp__header__btn__icon,
                styles.popUp__header__btn__right__icon,
                {
                  [styles.popUp__header__btn__right__icon__disabled]: isSaveDisabled,
                  [styles.popUp__header__btn__right__icon__darkTheme]: isDarkTheme,
                  [styles.popUp__header__btn__right__icon__disabled__darkTheme]: isDarkTheme && isSaveDisabled
                }
              )}
            />
          </button>
        </div>
        <div
          ref={listRef}
          className={classNames(styles.popUp__content, { [styles.popUp__content__darkTheme]: isDarkTheme })}
        >
          {items.map(({ label, value, isDisabled }) => {
            const isSelected = value === currentValue;
            const tooltipAttrs =
              isDisabled && isSelectionPage
                ? {
                    'data-tooltip-id': 'tooltip',
                    'data-tooltip-html': t('marketGraph.tooltips.selectionUnavailable'),
                    'data-tooltip-class-name': styles.popUp__item__tooltip
                  }
                : {};

            return (
              <button
                key={value}
                onClick={() => handleSelectValue({ value, isDisabled })}
                {...tooltipAttrs}
                className={classNames(styles.popUp__item, {
                  [styles.popUp__item__active]: isSelected,
                  [styles.popUp__item__disabled]: isDisabled,
                  [styles.popUp__item__darkTheme]: isDarkTheme
                })}
              >
                {label}
                {isSelected && (
                  <i
                    className={classNames('biab_custom-icon-checkmark', styles.popUp__item__icon, {
                      [styles.popUp__item__icon__disabled]: isDisabled,
                      [styles.popUp__item__icon__darkTheme]: isDarkTheme,
                      [styles.popUp__item__icon__disabled__darkTheme]: isDarkTheme && isDisabled
                    })}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
